<template>
  <div v-if="visible">
    <v-text-field
      v-if="withBar"
      v-model.lazy="search"
      clearable
      :label="t('global.search')"
      @update:model-value="onSearchChange"
      @click:clear="onSearchChange"
    >
      <template #prepend-inner>
        <v-icon
          icon="fas fa-search"
          size="x-small"
        />
      </template>
      <template #append>
        <slot name="append" />
      </template>
    </v-text-field>
    <v-alert
      v-if="(items.length === 0) && (childComponentItems.length > 0)"
      type="error"
    >
      <i18n-t keypath="search.no_results">
        <template #query>
          <span class="font-weight-semibold">"{{ search }}"</span>
        </template>
      </i18n-t>
    </v-alert>
    <v-alert
      v-if="(childComponentItems.length === 0)"
      type="info"
      :text="t('states.loading')"
    />
    <component
      :is="childComponent"
      v-bind="childComponentProperties"
      :key="componentKey"
      :items="items"
    />
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDataTableUtilities } from '@/composables';

const props = withDefaults(defineProps<{
  childComponent: object;
  childComponentItems: Array<any>;
  childComponentProperties?: object;
  itemsSearchFields: Array<any>;
  withBar?: boolean;
}>(), {
  withBar: () => true,
});

const { t } = useI18n();
const {
  watchString,
  search,
} = useDataTableUtilities();
watchString(search, 'search', '');
const items = ref<Array<Record<string, any>>>([]);
const componentKey = ref(0);
const visible = ref(false);

function onSearchChange() {
  items.value = props.childComponentItems.filter((p) => {
    if (!search.value) {
      search.value = '';
    }
    // Split search phrase into words, ignore empty
    const words = search.value.trim().split(/\s+/).filter(Boolean);
    // Each word must be found in at least one of the search fields
    return words.every(word =>
      props.itemsSearchFields.some(f =>
        (p[f] || '').toLowerCase().includes(word.toLowerCase()),
      ),
    );
  });
  componentKey.value += 1;
}

function refresh() {
  onSearchChange();
}

onMounted(() => {
  items.value = props.childComponentItems;
  props.itemsSearchFields.forEach((f) => {
    if (items.value.some((p) => p[f] === undefined)) {
      throw new Error(`Each item must contain a '${f}' field to search on.`);
    }
    else {
      visible.value = true;
    }
  });
});

watch(() => props.childComponentItems, () => {
  refresh();
});
</script>
